import { BrowserRouter, Outlet, Route, Routes } from "react-router-dom";
import App from "./App";
import Admin from "./pages/Admin";
import Contest from "./pages/Contest";
import ContestsRoute from "./pages/ContestsRoute";
import EditContest from "./pages/EditContest";
import Dashboard from "./pages/Dashboard";
import EarningsCalculatorPage from "./pages/EarningsCalculator";
import HowItWorksPage from "./pages/HowItWorks";
import Ovalord from "./pages/Ovalord";
import SiteNav from "./layout/SiteNav";
import Login from "./pages/Login";
import NewContest from "./pages/NewContest";
import NotFound from "./pages/NotFound";
import Producer from "./pages/Producer";
import Ranking from "./pages/Ranking";
import Register from "./pages/Register";
import Websites from "./pages/Websites";
import { SessionProvider } from "./session";

// The paths this app renders. Everything else on the domain is served by Go,
// so the CloudFront allowlist must match this list. Entries are exact paths,
// or a prefix ending in "/*" — the same notation the edge router uses.
export const SPA_PATHS: readonly string[] = [
  "/",
  "/login",
  "/register",
  "/websites-for-producers",
  "/contests",
  "/ranking",
  "/dashboard",
  "/admin",
  "/admin/contests/*",
  "/how-it-works",
];

export function isSpaPath(path: string): boolean {
  const p = path !== "/" && path.endsWith("/") ? path.slice(0, -1) : path;
  return SPA_PATHS.some((entry) => (entry.endsWith("/*") ? p.startsWith(entry.slice(0, -1)) : p === entry));
}

function Layout() {
  return (
    <>
      <SiteNav />
      <Outlet />
    </>
  );
}

// The session is read once, above every route, so moving between pages
// never refetches it. The auth pages carry their own shell, not the nav.
export function AppRoutes() {
  return (
    <SessionProvider>
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route element={<Layout />}>
          <Route path="/" element={<App />} />
          <Route path="/contests" element={<ContestsRoute />} />
          {/* Go serves /contest/{id} (the shell, or the Plush page), so it is
              deliberately not in SPA_PATHS. */}
          <Route path="/contest/:slug" element={<Contest />} />
          <Route path="/ranking" element={<Ranking />} />
          {/* Go serves /p/{username} (the shell with the producer's preview
              tags), so it is deliberately not in SPA_PATHS either. */}
          <Route path="/p/:username" element={<Producer />} />
          <Route path="/dashboard" element={<Dashboard />} />
          <Route path="/admin" element={<Admin />} />
          <Route path="/admin/contests/new" element={<NewContest />} />
          <Route path="/admin/contests/:id" element={<EditContest />} />
          <Route path="/websites-for-producers" element={<Websites />} />
          <Route path="/how-it-works" element={<HowItWorksPage />} />
          {/* Go serves /earnings-calculator (the shell with its own title and
              description, for search), so it is deliberately not in SPA_PATHS. */}
          <Route path="/earnings-calculator" element={<EarningsCalculatorPage />} />
          <Route path="/ovalord" element={<Ovalord />} />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </SessionProvider>
  );
}

export default function Root() {
  return (
    <BrowserRouter>
      <AppRoutes />
    </BrowserRouter>
  );
}
